import type {
  StripeCardCvcElementChangeEvent,
  StripeCardExpiryElementChangeEvent,
  StripeCardNumberElementChangeEvent,
} from "@stripe/stripe-js";
import type { ReactNode } from "react";

import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  Elements,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { useEffect, useMemo, useState } from "react";

import { Button } from "@/components/button";
import { FieldError, Input, Label, TextField } from "@/components/field";
import { focusRingStyle } from "@/components/utils";
import { cn } from "@/utils/cn";

import type {
  CardPaymentMethod,
  PaymentMethod,
  PaymentMethodFormProps,
} from "../types";

import { CardBrandMark } from "../../CardBrandMark";
import { formatExpiry } from "../utils/payment-method-format";
import { digitsOnly, zipCodeSchema } from "../utils/payment-method-schema";
import { getStripe, getStripeElementStyle } from "../utils/stripe";

type StripeFieldName = "cardCvc" | "cardExpiry" | "cardNumber";

type StripeFieldState = { error?: string; isComplete: boolean };

type StripeChangeEvent =
  | StripeCardCvcElementChangeEvent
  | StripeCardExpiryElementChangeEvent
  | StripeCardNumberElementChangeEvent;

interface CardPaymentFormProps
  extends PaymentMethodFormProps<CardPaymentMethod> {
  readonly setAsDefault: boolean;
}

export function CardPaymentForm(props: CardPaymentFormProps) {
  const stripe = getStripe();

  if (!stripe) {
    return (
      <p className="text-sm text-text-error-primary" role="alert">
        Card payments are unavailable right now. Please try again later.
      </p>
    );
  }

  return (
    <Elements stripe={stripe}>
      <CardPaymentFields {...props} />
    </Elements>
  );
}

const EMPTY_FIELDS: Record<StripeFieldName, StripeFieldState> = {
  cardCvc: { isComplete: false },
  cardExpiry: { isComplete: false },
  cardNumber: { isComplete: false },
};

function CardPaymentFields({
  currentMethod,
  formId,
  onSubmit,
  onValidityChange,
  setAsDefault,
}: CardPaymentFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const style = useMemo(getStripeElementStyle, []);
  const [focusedField, setFocusedField] = useState<null | StripeFieldName>(
    null,
  );
  const [fields, setFields] = useState(EMPTY_FIELDS);
  const [zipCode, setZipCode] = useState("");
  const [isZipTouched, setIsZipTouched] = useState(false);
  const [isReplacing, setIsReplacing] = useState(!currentMethod);

  const zipError = zipCodeSchema.safeParse(zipCode).error?.issues[0]?.message;
  const keepsCurrentMethod = !!currentMethod && !isReplacing;
  const isValid =
    !!stripe &&
    (keepsCurrentMethod
      ? setAsDefault
      : Object.values(fields).every((field) => field.isComplete) && !zipError);

  useEffect(() => {
    onValidityChange(isValid);
  }, [isValid, onValidityChange]);

  const fieldHandlers = (name: StripeFieldName) => ({
    onBlur: () => setFocusedField(null),
    onChange: (event: StripeChangeEvent) =>
      setFields((previous) => ({
        ...previous,
        [name]: { error: event.error?.message, isComplete: event.complete },
      })),
    onFocus: () => setFocusedField(name),
  });

  const cancelReplace = () => {
    setIsReplacing(false);
    setFields(EMPTY_FIELDS);
    setFocusedField(null);
    setZipCode("");
    setIsZipTouched(false);
  };

  const resolveMethod = async (): Promise<PaymentMethod> => {
    if (keepsCurrentMethod) return currentMethod;

    const card = elements?.getElement(CardNumberElement);

    if (!stripe || !card) {
      throw new Error("Card payments are unavailable right now.");
    }

    const { error, paymentMethod } = await stripe.createPaymentMethod({
      billing_details: { address: { postal_code: zipCode } },
      card,
      type: "card",
    });

    if (error || !paymentMethod.card) {
      throw new Error(error?.message ?? "We couldn't save this card.");
    }

    return {
      brand: paymentMethod.card.brand,
      expiryMonth: paymentMethod.card.exp_month,
      expiryYear: paymentMethod.card.exp_year,
      id: paymentMethod.id,
      last4: paymentMethod.card.last4,
      type: "card",
    };
  };

  return (
    <form
      className="flex flex-col gap-xl"
      id={formId}
      onSubmit={(event) => {
        event.preventDefault();
        if (isValid) onSubmit(resolveMethod);
      }}
    >
      {currentMethod && (
        <div className="flex items-center gap-lg">
          <CardBrandMark brand={currentMethod.brand} />
          <div className="flex min-w-0 flex-1 flex-col">
            <span className="text-sm font-medium text-text-primary">
              •••• {currentMethod.last4}
            </span>
            <span className="text-xs text-text-secondary">
              exp{" "}
              {formatExpiry(
                currentMethod.expiryMonth,
                currentMethod.expiryYear,
              )}
            </span>
          </div>
          <Button
            className="p-0 hover:bg-transparent"
            color={isReplacing ? "gray" : "color"}
            onPress={isReplacing ? cancelReplace : () => setIsReplacing(true)}
            size="sm"
            variant="tertiary"
          >
            {isReplacing ? "Cancel" : "Replace card"}
          </Button>
        </div>
      )}

      {isReplacing && (
        <div className="grid grid-cols-1 gap-xl sm:grid-cols-2">
          <StripeField
            error={fields.cardNumber.error}
            isFocused={focusedField === "cardNumber"}
            label="Card number"
          >
            <CardNumberElement
              options={{
                disableLink: true,
                placeholder: "1234 5678 9012 3456",
                style,
              }}
              {...fieldHandlers("cardNumber")}
            />
          </StripeField>

          <StripeField
            error={fields.cardCvc.error}
            isFocused={focusedField === "cardCvc"}
            label="CVC"
          >
            <CardCvcElement
              options={{ placeholder: "123", style }}
              {...fieldHandlers("cardCvc")}
            />
          </StripeField>

          <StripeField
            error={fields.cardExpiry.error}
            isFocused={focusedField === "cardExpiry"}
            label="Expiry date"
          >
            <CardExpiryElement
              options={{ placeholder: "MM/YY", style }}
              {...fieldHandlers("cardExpiry")}
            />
          </StripeField>

          <TextField
            isInvalid={isZipTouched && !!zipError}
            onBlur={() => setIsZipTouched(true)}
            onChange={(value) => setZipCode(digitsOnly(value, 5))}
            validationBehavior="aria"
            value={zipCode}
          >
            <Label className="text-text-primary">ZIP code</Label>
            <Input
              autoComplete="postal-code"
              inputMode="numeric"
              placeholder="12345"
            />
            <FieldError>{zipError}</FieldError>
          </TextField>
        </div>
      )}
    </form>
  );
}

function StripeField({
  children,
  error,
  isFocused,
  label,
}: {
  children: ReactNode;
  error?: string;
  isFocused: boolean;
  label: string;
}) {
  return (
    <div className="flex flex-col gap-sm">
      <Label className="text-text-primary" elementType="span">
        {label}
      </Label>
      <div
        className={cn(
          "rounded-md border border-border-primary bg-bg-primary px-[14px] py-[10px]",
          isFocused && focusRingStyle,
          error && "border-border-error",
        )}
      >
        {children}
      </div>
      {error && (
        <span className="text-sm text-text-error-primary" role="alert">
          {error}
        </span>
      )}
    </div>
  );
}
